import { ScrollArea } from '@/components/ui/scroll-area'
import { useAi } from '@/context/ai'
import { useBookmarkItems } from '@/context/bookmarkedItems'
import { soonestNonExpiredSession } from '@/lib/session'
import type { EnrichedClassMtv4SearchResult } from '@/shared/api-generated-types'
import LiveClassCard from '@/widgets/v2/AI/Cards/LiveClass'
import SkeletonCard from '@/widgets/v2/AI/Cards/Skeleton'
import { uniqBy } from 'lodash'
import { useEffect } from 'react'
import { gql, useQuery } from 'urql'

const MAX_RECOMMENDATIONS = 20
const MAX_DISPLAY_CARDS = 4
const MAX_CARD_HEIGHT = 125

const QUERY_SEARCH_AI_LIVE_CLASSES = gql`
  query SearchAiClasses($input: AiSearchMTV4Input!) {
    searchAiClassesMTV4(input: $input) {
      items {
        id
        title
        imageUrl
        guideName
        guideAvatarUrl
        durationSeconds
        slug
        guideId
        bookmarked
        aiSessions {
          start
          iid
        }
      }
    }
  }
`

interface Props {
  tenantId?: string | null
}

const RelatedLiveClasses = (props: Props): JSX.Element => {
  const {
    state: { query: searchQuery, relatedLiveClasses },
    dispatch,
  } = useAi()
  const { state: bookmarkState } = useBookmarkItems()

  const [{ data, fetching }] = useQuery<{
    searchAiClassesMTV4: { items: EnrichedClassMtv4SearchResult[] }
  }>({
    query: QUERY_SEARCH_AI_LIVE_CLASSES,
    pause: searchQuery.trim() === '',
    variables: { input: { query: searchQuery, limit: 5, tenant: props.tenantId } },
    requestPolicy: 'cache-first',
  })

  const getSoonestSessionStartTime = (classItem: EnrichedClassMtv4SearchResult): number => {
    const soonestSession = soonestNonExpiredSession(classItem.aiSessions, classItem.durationSeconds)
    return soonestSession?.start ? new Date(soonestSession.start).getTime() : Infinity //if no sessions push it to last
  }

  useEffect(() => {
    if (fetching || !data || !data.searchAiClassesMTV4?.items.length) return
    //Sort LiveClasses according to the soonest upcoming session
    const sortedClasses = [...data.searchAiClassesMTV4.items, ...relatedLiveClasses].sort((a, b) => {
      return getSoonestSessionStartTime(a) - getSoonestSessionStartTime(b)
    })

    dispatch({
      type: 'UPDATE',
      payload: { relatedLiveClasses: uniqBy(sortedClasses, 'id').slice(0, MAX_RECOMMENDATIONS) },
    })
  }, [data, fetching])

  return (
    <ScrollArea
      style={{
        height: `${MAX_CARD_HEIGHT * (relatedLiveClasses.length > 0 ? MAX_DISPLAY_CARDS : 1)}px`,
      }}
    >
      {fetching && <SkeletonCard />}
      {relatedLiveClasses.map((enrichedClass) => (
        <LiveClassCard
          key={enrichedClass.id}
          image={enrichedClass.imageUrl}
          {...enrichedClass}
          bookmarked={
            bookmarkState.classes?.[enrichedClass.id] !== undefined
              ? bookmarkState.classes?.[enrichedClass.id]
              : enrichedClass.bookmarked
          }
        />
      ))}
    </ScrollArea>
  )
}

export default RelatedLiveClasses
